import type { ChatApi, ChatMessageApi } from './types.ts';

/** True while Finlay is still writing a reply: the open chat checks again until it changes. */
export const hasPendingReply = (chat: ChatApi | undefined) =>
  chat?.messages.some((message) => message.status === 'Pending') ?? false;

/**
 * The newest completed reply, sent with the next message. When another tab's turn has added a newer one, finlay-api
 * knows this message was written without seeing it, so it can't confirm what that reply proposed.
 */
export const lastSeenReplyId = (messages: ChatMessageApi[]): string | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === 'Assistant' && message.status === 'Completed') {
      return message.id;
    }
  }
  return null;
};

/**
 * The chat as finlay-api holds it once a message is sent: the message, then a pending reply. Shown until the send
 * returns, and matched by what the API saves first, so checking the chat meanwhile changes nothing on screen.
 */
export const withPendingTurn = (
  chat: ChatApi,
  text: string,
  sentAt: string,
): ChatApi => ({
  ...chat,
  messages: [
    ...chat.messages,
    pendingMessage('message', 'User', text, 'Completed', sentAt),
    pendingMessage('reply', 'Assistant', '', 'Pending', sentAt),
  ],
});

/** A chat being started, as it shows until finlay-api answers with it: the first message and a pending reply. */
export const startingTurn = (text: string, sentAt: string) =>
  withPendingTurn({ id: '', title: null, messages: [] }, text, sentAt).messages;

const SENDING_ID = 'sending';

const pendingMessage = (
  id: string,
  role: ChatMessageApi['role'],
  content: string,
  status: ChatMessageApi['status'],
  createdAt: string,
): ChatMessageApi => ({
  id: `${SENDING_ID}-${id}`,
  role,
  content,
  status,
  results: null,
  createdAt,
});

/** The chat without the turn `withPendingTurn` added, for a send that never reached finlay-api. */
export const withoutPendingTurn = (chat: ChatApi): ChatApi => ({
  ...chat,
  messages: chat.messages.filter(
    (message) => !message.id.startsWith(SENDING_ID),
  ),
});

/**
 * Whether finlay-api saved this message after the first `countBefore` messages, so a send whose connection broke
 * did go through and must not be sent again.
 */
export const hasSavedMessage = (
  chat: ChatApi | undefined,
  text: string,
  countBefore: number,
) =>
  chat?.messages
    .slice(countBefore)
    .some(
      (message) =>
        message.role === 'User' &&
        message.content === text &&
        !message.id.startsWith(SENDING_ID),
    ) ?? false;

const statusOf = (error: unknown) =>
  error && typeof error === 'object' && 'status' in error
    ? error.status
    : undefined;

/**
 * finlay-api answered a send with a 4xx, so the message wasn't sent and goes back in the box. Anything else, such as
 * a connection Cloud Run cut after 300 seconds, may have sent it: the turn keeps running, so the chat is checked.
 */
export const isRefusedSend = (error: unknown) => {
  const status = statusOf(error);
  return typeof status === 'number' && status >= 400 && status < 500;
};

/** Another tab's turn in this chat ran past finlay-api's wait (409 `copilot_busy`). */
export const isBusyChat = (error: unknown) => statusOf(error) === 409;
